import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Reveal, SectionHeading } from "./Reveal";
import { MapPin, Phone, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full text-sm text-slate-900 bg-white border border-slate-300 rounded-xl px-4 py-3.5 outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-200 placeholder:text-slate-400";

export const Contact = () => {
  const { t } = useLang();
  const ORG_TYPES = t("contact.orgs");
  const NEEDS = t("contact.needsList");

  const [form, setForm] = useState({ name: "", email: "", org: 0, message: "" });
  const [needs, setNeeds] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        org: ORG_TYPES[form.org],
        needs: NEEDS.filter((_, i) => needs[i]),
        message: form.message,
      });
      setSent(true);
      toast.success(t("contact.toast"));
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-[#F8FAFC] bg-blueprint relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("contact.eyebrow")}
          title={
            <>
              {t("contact.titleA")}
              <span className="text-blue-700">{t("contact.titleB")}</span>
              {t("contact.titleC") || ""}
            </>
          }
          sub={t("contact.sub")}
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            <div className="relative p-7 sm:p-10 rounded-3xl border border-slate-200 bg-white shadow-[0_24px_60px_-28px_rgba(15,23,42,0.25)] min-h-[480px]">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="done"
                    data-testid="contact-success-panel"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 grid place-items-center p-10 text-center"
                  >
                    <div>
                      <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                      </span>
                      <h3 className="mt-6 text-2xl font-extrabold text-slate-900 tracking-tight">{t("contact.successTitle")}</h3>
                      <p className="mt-3 text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">
                        {t("contact.successA")}{form.name.split(" ")[0] || ""}{t("contact.successB")}{" "}
                        <span className="font-semibold text-slate-700">{form.email}</span>{t("contact.successC")}
                      </p>
                      <button
                        data-testid="contact-reset-button"
                        onClick={() => { setSent(false); setForm({ name: "", email: "", org: 0, message: "" }); setNeeds({}); }}
                        className="mt-7 text-sm font-bold text-blue-700 hover:text-slate-900 underline underline-offset-4"
                      >
                        {t("contact.reset")}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -14 }}
                    onSubmit={submit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">{t("contact.name")}</label>
                        <input
                          data-testid="contact-name-input"
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder={t("contact.namePh")}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">{t("contact.email")}</label>
                        <input
                          data-testid="contact-email-input"
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder={t("contact.emailPh")}
                          className={inputCls}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">{t("contact.org")}</label>
                      <div className="flex flex-wrap gap-2">
                        {ORG_TYPES.map((o, i) => (
                          <button
                            type="button"
                            key={o}
                            data-testid={`contact-org-${i}`}
                            onClick={() => setForm({ ...form, org: i })}
                            className={`text-xs font-bold px-4 py-2.5 rounded-full border transition-all duration-200 ${
                              form.org === i ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                            }`}
                          >
                            {o}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">{t("contact.needs")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {NEEDS.map((n, i) => (
                          <label key={n} data-testid={`contact-need-${i}`} className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!needs[i]}
                              onChange={(e) => setNeeds({ ...needs, [i]: e.target.checked })}
                              className="w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                            />
                            {n}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">{t("contact.message")}</label>
                      <textarea
                        data-testid="contact-message-input"
                        rows={4}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder={t("contact.msgPh")}
                        className={`${inputCls} resize-none`}
                      />
                    </div>

                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={sending}
                      className="group w-full sm:w-auto inline-flex justify-center items-center gap-2 text-sm font-bold text-white bg-blue-700 hover:bg-slate-900 disabled:opacity-60 disabled:cursor-not-allowed px-8 py-4 rounded-full shadow-[0_14px_32px_-12px_rgba(29,78,216,0.55)] transition-all duration-300 hover:-translate-y-0.5"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("contact.submit")}
                      {!sending && <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="h-full flex flex-col gap-5">
              <div className="p-7 rounded-3xl bg-slate-900 text-white relative overflow-hidden flex-1">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-blue-600/25 blur-2xl" />
                <h3 className="text-lg font-extrabold tracking-tight">{t("contact.officeTitle")}</h3>
                <div className="mt-6 space-y-5 text-sm">
                  <p className="flex items-start gap-3 text-slate-300">
                    <MapPin className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                    <span>
                      {t("contact.addr").map((line, i) => (
                        <span key={i}>{line}{i < t("contact.addr").length - 1 && <br />}</span>
                      ))}
                    </span>
                  </p>
                  <p className="flex items-center gap-3 text-slate-300">
                    <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                    {t("contact.helpdesk")}
                  </p>
                  <p className="flex items-center gap-3 text-slate-300">
                    <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                    office@qsafeindia.com
                  </p>
                </div>
              </div>
              <div className="p-6 rounded-3xl border border-emerald-200 bg-emerald-50/70">
                <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-emerald-700 uppercase">{t("contact.gemLabel")}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("contact.gemBody")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
